import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import GameShell from "@/components/GameShell";
import BetControls from "@/components/BetControls";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";

const SYM = {
  7: { g: "7", name: "Lucky 7" },
  D: { g: "💎", name: "Diamond" },
  B: { g: "₿", name: "Bitcoin" },
  E: { g: "Ξ", name: "Ether" },
  S: { g: "◎", name: "Solana" },
  T: { g: "₮", name: "Tether" },
  R: { g: "★", name: "Star" },
  C: { g: "🪙", name: "Coin" },
  X: { g: "👑", name: "Wild" },
  Z: { g: "⚡", name: "Scatter" },
};
const KEYS = Object.keys(SYM);
const LINE_COLORS = ["#FFC107", "#00F0FF", "#FF3366", "#00E676", "#A78BFA"];
const IDLE = [["7", "D", "B"], ["X", "7", "E"], ["S", "X", "T"], ["R", "C", "X"], ["Z", "B", "7"]];
const NONE = [false, false, false, false, false];
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function Reel({ symbols, spinning, hits, reel }) {
  return (
    <div className="flex flex-col gap-2 overflow-hidden mega-reel" data-testid={`mega-reel-${reel}`}>
      {spinning ? (
        <div className="flex flex-col gap-2 spinning">
          {[...KEYS, ...KEYS].map((k, i) => (
            <div key={i} className="mega-cell"><span className="gold-text">{SYM[k].g}</span></div>
          ))}
        </div>
      ) : (
        symbols.map((k, row) => {
          const hit = hits.get(`${reel}-${row}`);
          return (
            <div
              key={row}
              className={`mega-cell ${hit ? "mega-hit" : ""}`}
              style={hit ? { borderColor: hit, boxShadow: `0 0 18px ${hit}66, inset 0 0 12px ${hit}33` } : {}}
              data-testid={`mega-cell-${reel}-${row}`}
            >
              <span className={k === "X" || k === "Z" ? "" : "gold-text"}>{SYM[k].g}</span>
            </div>
          );
        })
      )}
    </div>
  );
}

export default function MegaSlots() {
  const { setUser, user } = useAuth();
  const { currency } = useCurrency();
  const [amount, setAmount] = useState(10);
  const [busy, setBusy] = useState(false);
  const [grid, setGrid] = useState(IDLE);
  const [spinning, setSpinning] = useState(NONE);
  const [outcome, setOutcome] = useState(null);
  const [info, setInfo] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => { api.get("/games/megaslots/info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const hits = useMemo(() => {
    const m = new Map();
    if (!outcome || !info) return m;
    outcome.wins.forEach((w) => {
      for (let c = 0; c < w.count; c++) m.set(`${c}-${info.paylines[w.line][c]}`, LINE_COLORS[w.line]);
    });
    if (outcome.scatter_mult > 0) {
      outcome.grid.forEach((reel, r) => reel.forEach((k, row) => { if (k === "Z") m.set(`${r}-${row}`, "#FFFFFF"); }));
    }
    return m;
  }, [outcome, info]);

  const placeBet = async (betAmount, quiet = false) => {
    setBusy(true);
    setOutcome(null);
    setSpinning([true, true, true, true, true]);
    let data;
    try {
      ({ data } = await api.post("/games/megaslots", { amount: Number(betAmount), currency }));
    } catch (e) {
      setSpinning(NONE);
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Failed");
      return null;
    }
    const res = data.bet.result;
    for (let i = 0; i < 5; i++) {
      await wait(i === 0 ? 650 : 220);
      setGrid((g) => { const n = [...g]; n[i] = res.grid[i]; return n; });
      setSpinning((s) => { const n = [...s]; n[i] = false; return n; });
    }
    setOutcome(res);
    setUser((u) => ({ ...u, balances: data.balances }));
    setRecent((r) => [{ mult: data.bet.multiplier, won: data.bet.profit > 0 }, ...r].slice(0, 12));
    if (!quiet) {
      if (data.bet.profit > 0) toast.success(`×${data.bet.multiplier} · +${formatAmount(data.bet.profit, currency)} ${currency}`);
      else toast.error("No win this spin");
    }
    setBusy(false);
    return data.bet;
  };

  const play = async () => {
    if (!user) return;
    if (Number(amount) <= 0) return toast.error("Enter a bet");
    await placeBet(amount);
  };

  return (
    <GameShell title="Mega Slots" subtitle="Five reels. Five lines. Crown wilds, lightning scatters and a 1000× lucky seven." accent="#FF3366"
      aside={
        <BetControls
          amount={amount}
          setAmount={setAmount}
          onPlay={play}
          autoPlaceBet={(a) => placeBet(a, true)}
          busy={busy}
          disabled={!user || busy}
          playLabel="Spin"
          extra={
            <div className="text-[10px] text-zinc-500 flex items-center justify-between">
              <span className="uppercase tracking-widest">5 lines · bet split per line</span>
              <span className="font-mono-num text-amber-400">RTP {info ? (info.rtp * 100).toFixed(1) : "—"}%</span>
            </div>
          }
        />
      }
    >
      <div className="flex flex-col items-center gap-6">
        <div className="relative panel p-4 md:p-6 bg-gradient-to-b from-[#1E1E24] to-[#0F0F11] w-full max-w-2xl" data-testid="mega-grid">
          <div className="grid grid-cols-5 gap-2 md:gap-3">
            {grid.map((reel, i) => <Reel key={i} reel={i} symbols={reel} spinning={spinning[i]} hits={hits} />)}
          </div>
          <div className="absolute -left-1 top-0 bottom-0 flex flex-col justify-around py-6">
            {[1, 0, 2].map((l) => <span key={l} className="w-1.5 h-1.5 rounded-full" style={{ background: LINE_COLORS[l] }} />)}
          </div>
        </div>

        <div className="text-xs uppercase tracking-widest text-zinc-500 min-h-[18px]" data-testid="mega-status">
          {busy ? "Spinning…" : outcome ? (outcome.total_mult > 0 ? `×${outcome.total_mult} · ${outcome.wins.length} line${outcome.wins.length === 1 ? "" : "s"}${outcome.scatter_mult ? ` + ${outcome.scatters} scatters` : ""}` : "No win. Spin again.") : "Line up the vault"}
        </div>

        {outcome?.wins?.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center" data-testid="mega-wins">
            {outcome.wins.map((w, i) => (
              <span key={i} className="chip font-mono-num" style={{ color: LINE_COLORS[w.line], borderColor: `${LINE_COLORS[w.line]}55` }}>
                Line {w.line + 1} · {SYM[w.symbol].g}×{w.count} · ×{w.mult}
              </span>
            ))}
            {outcome.scatter_mult > 0 && <span className="chip font-mono-num text-white">⚡×{outcome.scatters} · ×{outcome.scatter_mult}</span>}
          </div>
        )}

        {recent.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap justify-center" data-testid="mega-recent">
            {recent.map((r, i) => (
              <span key={i} className={`chip font-mono-num ${r.won ? "text-emerald-400 border-emerald-500/30" : "text-zinc-500"}`}>×{r.mult}</span>
            ))}
          </div>
        )}

        {info && (
          <div className="w-full max-w-2xl" data-testid="mega-paytable">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Paytable · per line bet</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(info.paytable).map(([k, pays]) => (
                <div key={k} className="panel px-3 py-2 flex items-center gap-3">
                  <span className="text-2xl gold-text font-display">{SYM[k].g}</span>
                  <div className="font-mono-num text-[11px] text-zinc-400 leading-tight">
                    <div>3× <span className="text-white">{pays[0]}</span></div>
                    <div>4× <span className="text-white">{pays[1]}</span></div>
                    <div>5× <span className="text-amber-400">{pays[2]}</span></div>
                  </div>
                </div>
              ))}
              <div className="panel px-3 py-2 flex items-center gap-3 col-span-2">
                <span className="text-2xl font-display">👑</span>
                <div className="text-[11px] text-zinc-400">Wild · substitutes for any paying symbol</div>
              </div>
              <div className="panel px-3 py-2 flex items-center gap-3 col-span-2">
                <span className="text-2xl font-display">⚡</span>
                <div className="font-mono-num text-[11px] text-zinc-400">
                  Scatter anywhere · 3× <span className="text-white">{info.scatter_pays[3]}</span> · 4× <span className="text-white">{info.scatter_pays[4]}</span> · 5× <span className="text-amber-400">{info.scatter_pays[5]}</span> total bet
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </GameShell>
  );
}
